import { useAuth0 } from '@auth0/auth0-react';
import { ANALYTICS_EVENTS } from '@syncanix/shared';
import { Button } from '@syncanix/ui';
import { useCallback, useEffect, useRef } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import { analytics } from '@/analytics/posthog-client';
import { OutsideShellHeader } from '@/components/OutsideShellHeader';
import { useT } from '@/core/providers/I18nProvider';
import { logger } from '@/lib/logger';

import { isEmailVerificationError } from './resend-verification';
import { VerifyEmailScreen } from './VerifyEmailScreen';

import type { ReactElement } from 'react';

interface FromState {
  from?: { pathname?: string; search?: string; hash?: string };
}

/**
 * Rebuild the pre-redirect destination as a full relative URL — pathname AND
 * `search` AND `hash`. Dropping `search` (the previous behaviour) silently broke
 * every auth-gated route that carries a meaningful query param through login: a
 * signed-out visitor to `/cli-auth?session=<id>` (the `syncanix login` / demo-
 * injector approval page) landed back on `/cli-auth` with the session id gone,
 * showing the "open the URL the CLI printed" empty state with nothing to approve.
 */
function fullReturnTo(from: FromState['from']): string {
  if (!from?.pathname) return '/home';
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

/**
 * Sign-in entry. If the user is already authenticated they get bounced to
 * their pre-redirect destination (or /home by default). Otherwise we kick
 * straight to the Auth0 hosted login — no intermediate "click to continue"
 * screen. The `?intent=signup` param (sent by the marketing CTAs) forwards
 * Auth0's signup screen hint. On an Auth0 error we surface a recoverable
 * screen with a manual retry rather than auto-looping the redirect.
 */
export function SignInPage(): ReactElement {
  const { t } = useT();
  const { isAuthenticated, isLoading, loginWithRedirect, error } = useAuth0();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = fullReturnTo((location.state as FromState | null)?.from);
  const isSignup = searchParams.get('intent') === 'signup';
  const hasRedirected = useRef(false);

  const startLogin = useCallback((): void => {
    hasRedirected.current = true;
    // Bridge the acquisition funnel across the auth boundary: this anonymous
    // /signin landing would otherwise vanish — posthog-js drops queued events
    // on the sub-2s redirect unload. `sendInstantly` flushes it now, before
    // loginWithRedirect navigates away. (A11.)
    analytics.capture(
      ANALYTICS_EVENTS.SIGNIN_PAGE_VIEWED,
      { intent: isSignup ? 'signup' : 'login' },
      { sendInstantly: true },
    );
    void loginWithRedirect({
      appState: { returnTo: from },
      ...(isSignup ? { authorizationParams: { screen_hint: 'signup' } } : {}),
    });
  }, [loginWithRedirect, from, isSignup]);

  useEffect(() => {
    if (error) {
      logger.error('[auth] sign-in error', { error });
    }
  }, [error]);

  useEffect(() => {
    if (isLoading || isAuthenticated || error || hasRedirected.current) {
      return;
    }
    startLogin();
  }, [isLoading, isAuthenticated, error, startLogin]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  if (error) {
    // Auth0's "Require verified email" gate denies with access_denied. That is
    // not a failure to apologize for — it's an expected next step, so show a
    // dedicated localized "verify your email" screen (with a resend) instead of
    // the generic error card (whose retry would just re-loop into the denial).
    if (isEmailVerificationError(error)) {
      return <VerifyEmailScreen onBackToSignIn={startLogin} />;
    }
    return (
      <div className="flex min-h-screen flex-col">
        <OutsideShellHeader />
        <main className="flex flex-1 items-center justify-center p-8">
          <section className="max-w-md space-y-6 text-center">
            <h1 className="text-h1">{t('signin.title')}</h1>
            <p role="alert" className="text-sm text-danger-foreground">
              {/* Show a localized generic — the raw Auth0 SDK message is English/technical and is
                already logged via logger.error for diagnostics (I94). */}
              {t('error.generic.body')}
            </p>
            <Button
              onClick={() => {
                startLogin();
              }}
            >
              {t('common.retry')}
            </Button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-8">
      <div role="status" aria-live="polite">
        {t('signin.redirecting')}
      </div>
    </main>
  );
}
